import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  Upload,
  FileText,
  BarChart3,
  Settings,
  Plus,
  Activity,
  TrendingUp,
  Clock,
  Target,
  Globe2,
  Zap,
  ArrowUp,
  Star,
  User,
  LogOut,
  PlayCircle,
  CheckCircle,
  XCircle,
  FolderPlus,
  Folder,
  MessageSquare,
  Edit,
  Download,
  UserCheck,
  FilePlus,
  UserPlus,
  Database,
  ChevronRight,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import InputForm from '../components/InputForm';
import { FormData, UserStats, UserActivity, UserActivitiesResponse } from '../types';
import { TenderAnalysisAPI, DashboardAPI } from '../services/api';
import { BentoGridItem } from '../components/BentoGrid';
import { PercentageCounter, NumberCounter } from '../components/AnimatedCounter';
import Particles from '../components/Particles';
import Globe from '../components/Globe';
import OnboardingDashboard from '../components/OnboardingDashboard';
import { TourProvider } from '../context/TourContext';
import TourOverlay from '../components/TourOverlay';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// =============================================================================
// PAGE SHELL — coherent with /profile, /settings, /knowledge-base, /tender-bank
// =============================================================================

const DashboardContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) so any future sticky element can attach to the
     viewport top instead of being trapped here. */
  overflow: clip;

  /* Preserve the existing inline-keyframe spinner pattern referenced by the
     onboarding view's loading spinner. */
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const GreetingTitle = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.025em;
  line-height: 1.1;

  & .name {
    color: ${theme.colors.primary[600]};
    font-weight: ${theme.typography.fontWeight.bold};
  }

  & .comma {
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.normal};
    margin-right: 4px;
  }
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 580px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    align-items: stretch;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

type StatusTone = 'success' | 'primary';

const StatusPill = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-variant-numeric: tabular-nums;
  }

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    ${({ $tone }) =>
      $tone === 'success'
        ? css`
            background: ${theme.colors.success[500]};
            box-shadow: 0 0 0 4px ${theme.colors.success[500]}24;
          `
        : css`
            background: ${theme.colors.primary[500]};
            box-shadow: 0 0 0 4px ${theme.colors.primary[500]}24;
          `}
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
  align-items: center;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const DateStamp = styled.div`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

// =============================================================================
// STATS GRID
// =============================================================================

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[5]};

  & > * {
    animation: ${fadeUp} 0.5s ease-out both;
  }
  & > *:nth-child(1) { animation-delay: 0ms; }
  & > *:nth-child(2) { animation-delay: 60ms; }
  & > *:nth-child(3) { animation-delay: 120ms; }
  & > *:nth-child(4) { animation-delay: 180ms; }

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }
`;

type StatTone = 'primary' | 'success' | 'warning' | 'accent';

const StatIconTile = styled.span<{ $tone: StatTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[600]};
          border-color: ${theme.colors.success[500]}40;
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[600]};
          border-color: ${theme.colors.warning[500]}40;
        `
      : $tone === 'accent'
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `
      : css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[500]};
          border-color: ${theme.colors.primary[100]};
        `}
`;

const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// Wrapper around AnimatedCounter so we can override its internal styling and
// match the established display-font numeric treatment used on every other
// stats grid in the app.
const StatValueWrap = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 30px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.025em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;

  /* Override AnimatedCounter's internal styling so the number inherits from
     this wrapper's display-font / -0.025em letter-spacing. */
  & > * {
    font-family: inherit !important;
    font-size: inherit !important;
    font-weight: inherit !important;
    color: inherit !important;
    letter-spacing: inherit !important;
    line-height: inherit !important;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

// =============================================================================
// SECONDARY PERFORMANCE PILL ROW (Improvement Rate + Avg Score)
// =============================================================================

const PerformanceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[8]};
`;

const PerformancePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]} 6px ${theme.spacing[2]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};

  & .icon {
    width: 22px;
    height: 22px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[600]};
    border: 1px solid ${theme.colors.primary[100]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  & .label {
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.medium};
    letter-spacing: 0.04em;
  }

  & .value {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-variant-numeric: tabular-nums;
    font-family: ${theme.typography.fontFamily.mono};
  }
`;

// =============================================================================
// TWO-COLUMN BODY (Activity + Quick Actions)
// =============================================================================

const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing[5]};
  margin-bottom: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]} ${theme.spacing[6]} ${theme.spacing[5]};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  flex-direction: column;
  min-height: 360px;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[5]};
  }
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[5]};
  letter-spacing: -0.015em;
`;

// -----------------------------------------------------------------------------
// Activity timeline (left column)
// -----------------------------------------------------------------------------

const Timeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  flex: 1;
`;

const TimelineItem = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[1]};
  animation: ${fadeUp} 0.45s ease-out both;

  /* Vertical journal-line connecting the icon tiles (only between items, not
     after the last). */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 18px;
    top: 36px;
    bottom: -${theme.spacing[3]};
    width: 1px;
    background: ${theme.colors.borderLight};
  }

  &:nth-child(1) { animation-delay: 0ms; }
  &:nth-child(2) { animation-delay: 50ms; }
  &:nth-child(3) { animation-delay: 100ms; }
  &:nth-child(4) { animation-delay: 150ms; }
  &:nth-child(5) { animation-delay: 200ms; }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TimelineIcon = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[600]};
  border: 1px solid ${theme.colors.primary[100]};
  flex-shrink: 0;
`;

const TimelineBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  padding-top: 6px;
`;

const TimelineText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  line-height: 1.5;
  margin: 0;
  letter-spacing: -0.005em;
`;

const TimelineTime = styled.span`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px;
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  margin-top: 1px;
`;

const TimelineFooter = styled.div`
  margin-top: ${theme.spacing[5]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px dashed ${theme.colors.borderLight};
`;

const TimelineMoreLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  background: none;
  border: none;
  padding: 0;
  color: ${theme.colors.primary[600]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: color ${theme.transitions.base};

  & .arrow {
    transition: transform ${theme.transitions.base};
  }

  &:hover {
    color: ${theme.colors.primary[700]};
  }

  &:hover .arrow {
    transform: translateX(2px);
  }
`;

// -----------------------------------------------------------------------------
// Quick Actions (right column)
// -----------------------------------------------------------------------------

const QuickActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  flex: 1;
`;

const QuickActionCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: transform ${theme.transitions.base}, border-color ${theme.transitions.base},
    box-shadow ${theme.transitions.base};
  text-align: left;
  font-family: inherit;
  width: 100%;

  &:hover {
    transform: translateY(-1px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 4px 10px -4px rgba(0, 0, 41, 0.10);
  }

  &:hover .qa-arrow {
    transform: translateX(3px);
    color: ${theme.colors.primary[600]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }
`;

const QuickActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[600]};
  border: 1px solid ${theme.colors.primary[100]};
  flex-shrink: 0;
`;

const QuickActionBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const QuickActionLabel = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.005em;
`;

const QuickActionMeta = styled.span`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  line-height: 1.4;
`;

const QuickActionArrow = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  transition: transform ${theme.transitions.base}, color ${theme.transitions.base};
  flex-shrink: 0;
`;

// =============================================================================
// GLOBAL OPPORTUNITIES — NAVY GRADIENT HERO
// =============================================================================

const GlobeHero = styled.div`
  position: relative;
  padding: ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.xl};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: rgba(255, 255, 255, 0.95);
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.primary[700]};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing[8]};
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: ${theme.spacing[8]};

  /* Grain texture for tactile depth on the deep navy. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.045 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
    z-index: -1;
  }

  /* Soft corner glow. */
  &::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    padding: ${theme.spacing[6]};
    text-align: left;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[6]} ${theme.spacing[5]};
  }
`;

const GlobeText = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  min-width: 0;
`;

const GlobeEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.65);
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.success[500]};
    box-shadow: 0 0 0 3px ${theme.colors.success[500]}33;
  }
`;

const GlobeTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 28px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0;
  letter-spacing: -0.025em;
  line-height: 1.15;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 22px;
  }
`;

const GlobeSub = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: ${theme.typography.fontSize.sm};
  margin: 0;
  max-width: 460px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const RegionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
`;

const RegionChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 5px ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-variant-numeric: tabular-nums;

  & .code {
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.bold};
    letter-spacing: 0.12em;
    color: #fff;
  }

  & .count {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const GlobeWrap = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    margin-top: ${theme.spacing[4]};
  }
`;

// =============================================================================
// "ELSEWHERE" QUICK-LINK CARDS
// =============================================================================

const ElsewhereSection = styled.section`
  margin-bottom: ${theme.spacing[8]};
`;

const ElsewhereHead = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const ElsewhereGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ElsewhereCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, border-color ${theme.transitions.base},
    box-shadow ${theme.transitions.base};
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }

  &:hover .ew-arrow {
    transform: translateX(3px);
    color: ${theme.colors.primary[600]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }
`;

const ElsewhereIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[600]};
  border: 1px solid ${theme.colors.primary[100]};
  flex-shrink: 0;
`;

const ElsewhereBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ElsewhereTitle = styled.span`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.012em;
`;

const ElsewhereMeta = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const ElsewhereArrow = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  transition: transform ${theme.transitions.base}, color ${theme.transitions.base};
`;

// =============================================================================
// LOADING SHELL
// =============================================================================

const LoadingShell = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.borderLight};
  border-top-color: ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// =============================================================================
// QUICK ACTIONS DATA (preserved verbatim — see brief)
// =============================================================================

const quickActions = [
  {
    icon: <Plus size={16} />,
    label: 'New Tender Response',
    action: 'new-analysis',
  },
  {
    icon: <Upload size={16} />,
    label: 'Upload Documents',
    action: 'upload',
  },
  {
    icon: <FileText size={16} />,
    label: 'New Evaluation',
    action: 'templates',
  },
  {
    icon: <Settings size={16} />,
    label: 'Settings',
    action: 'settings',
  },
];

// Per-action sub-meta strings for the refined card-button. These describe what
// each quick action does — adds context without bloating the card height.
const quickActionMetas: Record<string, string> = {
  'new-analysis': 'Run a fresh tender analysis end-to-end',
  upload: 'Add documents to your tender bank',
  templates: 'Start a fresh evaluation flow',
  settings: 'Adjust preferences and account details',
};

// Generate dashboard stats using actual user data (PRESERVED — full 6-stat array.
// The hero presents the first four; the bottom two surface as a Performance pill row.)
const getDashboardStats = (stats: {
  projects: number;
  activeProjects: number;
  analyses: number;
  tenderBankItems: number;
  successRate: number;
  improvementRate: number;
  averageResponseScore: number;
  timeSavedHours: number;
  isLoading: boolean;
}) => [
  { value: stats.analyses, label: 'Total Analyses', icon: <BarChart3 size={20} />, type: 'number' },
  { value: stats.activeProjects, label: 'Active Projects', icon: <Target size={20} />, type: 'number' },
  { value: stats.successRate, label: 'Success Rate', icon: <TrendingUp size={20} />, type: 'percentage' },
  { value: stats.timeSavedHours, label: 'Time Saved', icon: <Zap size={20} />, type: 'number', suffix: ' hrs' },
  { value: stats.improvementRate, label: 'Response Improvement Rate', icon: <ArrowUp size={20} />, type: 'percentage' },
  { value: stats.averageResponseScore, label: 'Average Response Score', icon: <Star size={20} />, type: 'number' },
];

// Helper function to get icon component from icon name (preserved verbatim)
const getActivityIcon = (iconName: string) => {
  const iconMap: { [key: string]: React.ReactElement } = {
    user: <User size={16} />,
    'log-out': <LogOut size={16} />,
    'play-circle': <PlayCircle size={16} />,
    'check-circle': <CheckCircle size={16} />,
    'x-circle': <XCircle size={16} />,
    upload: <Upload size={16} />,
    'folder-plus': <FolderPlus size={16} />,
    folder: <Folder size={16} />,
    'message-square': <MessageSquare size={16} />,
    edit: <Edit size={16} />,
    download: <Download size={16} />,
    'user-check': <UserCheck size={16} />,
    settings: <Settings size={16} />,
    'file-plus': <FilePlus size={16} />,
    'user-plus': <UserPlus size={16} />,
    database: <Database size={16} />,
    activity: <Activity size={16} />,
    // Fallback
    'file-text': <FileText size={16} />,
    'bar-chart-3': <BarChart3 size={16} />,
  };

  return iconMap[iconName] || <Activity size={16} />;
};

// Fallback activities if no real activities are available (preserved)
const fallbackActivities: UserActivity[] = [
  {
    id: 'fallback-1',
    type: 'welcome',
    description: 'Welcome to OpenTender AI! Start your first analysis.',
    time: 'Getting started',
    icon: 'file-text',
  },
];

/**
 * Static demo activity used to pad the timeline for walkthroughs.
 *
 * A new or lightly-used account returns one or two real events, which leaves
 * the panel looking broken rather than empty. These entries top the list up
 * and mirror the ARA-2026-ITS-014 evaluation in the Evaluation workspace, so
 * the dashboard and that flow tell the same story.
 *
 * Real activity always sorts above these. Delete this array and the
 * `padActivities` call below to return to live-only behaviour.
 */
const demoActivities: UserActivity[] = [
  {
    id: 'demo-1',
    type: 'evaluation_award',
    description: 'Award recommendation approved — Strathmere Solutions Ltd, 90.07 of 100',
    time: '12 minutes ago',
    icon: 'check-circle',
  },
  {
    id: 'demo-2',
    type: 'evaluation_moderation',
    description: 'Moderation completed — 6 divergences resolved across 45 assessments',
    time: '1 hour ago',
    icon: 'user-check',
  },
  {
    id: 'demo-3',
    type: 'evaluation_scored',
    description: 'Assessment run completed — 5 compliant tenders scored, 55 evidence citations',
    time: '2 hours ago',
    icon: 'bar-chart-3',
  },
  {
    id: 'demo-4',
    type: 'evaluation_excluded',
    description: '2 tenders excluded at the compliance gate — Pellworth IT Services, Brayford Technology Partners',
    time: '3 hours ago',
    icon: 'x-circle',
  },
  {
    id: 'demo-5',
    type: 'submissions_ingested',
    description: '7 tender submissions ingested — 1,343 pages indexed and hashed',
    time: 'Yesterday',
    icon: 'upload',
  },
  {
    id: 'demo-6',
    type: 'evaluation_model',
    description: 'Evaluation model published — Provision of Managed IT Services 2027–2030',
    time: '2 days ago',
    icon: 'file-plus',
  },
  {
    id: 'demo-7',
    type: 'knowledge_base',
    description: 'Tender bank updated — 3 documents added to the knowledge base',
    time: '4 days ago',
    icon: 'database',
  },
];

/** Real events first, demo entries filling the remainder up to `target`. */
const padActivities = (real: UserActivity[], target = 7): UserActivity[] =>
  real.length >= target ? real.slice(0, target) : [...real, ...demoActivities].slice(0, target);

// Function to filter and deduplicate activities (preserved verbatim)
const filterAndDeduplicateActivities = (activities: UserActivity[]): UserActivity[] => {
  const loginLogoutTypes = ['login', 'logout', 'logged_in', 'logged_out', 'sign_in', 'sign_out'];
  const otherActivities: UserActivity[] = [];
  const loginLogoutMap: { [key: string]: UserActivity } = {};

  // Separate activities and find most recent login/logout events
  activities.forEach(activity => {
    const activityType = activity.type.toLowerCase();
    const description = activity.description.toLowerCase();

    // Check if this is a login/logout activity
    const isLoginLogout = loginLogoutTypes.some(
      type =>
        activityType.includes(type) ||
        description.includes('logged in') ||
        description.includes('logged out') ||
        description.includes('signed in') ||
        description.includes('signed out') ||
        description.includes('login') ||
        description.includes('logout')
    );

    if (isLoginLogout) {
      // Determine if it's login or logout
      const isLogin =
        activityType.includes('login') ||
        activityType.includes('logged_in') ||
        activityType.includes('sign_in') ||
        description.includes('logged in') ||
        description.includes('signed in') ||
        description.includes('login');

      const key = isLogin ? 'login' : 'logout';

      // Keep only the most recent of each type
      // Since UserActivity doesn't have created_at, we'll use a simple approach
      // and assume activities are ordered by recency (newest first)
      if (!loginLogoutMap[key]) {
        loginLogoutMap[key] = activity;
      }
    } else {
      // Keep all other activities
      otherActivities.push(activity);
    }
  });

  // Combine other activities with deduplicated login/logout activities
  const deduplicatedLoginLogout = Object.values(loginLogoutMap);
  const allActivities = [...otherActivities, ...deduplicatedLoginLogout];

  // Since activities come pre-sorted from the API and UserActivity uses relative time strings,
  // we'll maintain the original order and just return the combined activities
  // The API should return activities in descending order (most recent first)
  return allActivities;
};

// =============================================================================
// COMPONENT
// =============================================================================

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { setFormData, setLoading, setError, setCurrentStep, setThreadId } = useAppContext();

  // State for determining onboarding vs dashboard view
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null); // null = loading
  const [userStats, setUserStats] = useState({
    projects: 0,
    activeProjects: 0,
    analyses: 0,
    tenderBankItems: 0,
    successRate: 0,
    improvementRate: 0,
    averageResponseScore: 0,
    timeSavedHours: 0,
    isLoading: true,
  });
  const [recentActivities, setRecentActivities] = useState<UserActivity[]>([]);

  // Check user data to determine if onboarding is needed
  useEffect(() => {
    const checkUserData = async () => {
      if (!authState.user?.id) {
        setShowOnboarding(true);
        return;
      }

      try {
        setUserStats(prev => ({ ...prev, isLoading: true }));

        // Try to get real user statistics from the API
        // If the backend endpoints aren't ready yet, we'll fall back to mock data
        try {
          const statsResponse = await DashboardAPI.getUserStats(authState.user.id);

          if (statsResponse.success && statsResponse.data) {
            const userStats = statsResponse.data as UserStats;
            const apiStats = {
              projects: userStats.projects_count || 0,
              activeProjects: userStats.active_projects_count || 0,
              analyses: userStats.analyses_count || 0,
              tenderBankItems: userStats.tender_bank_count || 0,
              successRate: userStats.success_rate || 0,
              improvementRate: userStats.improvement_rate || 0,
              averageResponseScore: userStats.average_response_score || 0,
              timeSavedHours: userStats.time_saved_hours || 0,
              isLoading: false,
            };

            setUserStats(apiStats);

            // Fetch user activities
            try {
              const activitiesResponse = await DashboardAPI.getUserActivities(
                authState.user.id,
                20
              ); // Fetch more to filter
              if (activitiesResponse.success && activitiesResponse.data) {
                const activitiesData = activitiesResponse.data as UserActivitiesResponse;
                const rawActivities = activitiesData.activities || [];

                // Filter and deduplicate activities
                const filteredActivities = filterAndDeduplicateActivities(rawActivities);
                setRecentActivities(filteredActivities.slice(0, 5)); // Limit to 5 items
              }
            } catch (activitiesError) {
              console.error('Failed to fetch activities:', activitiesError);
            }

            // Show onboarding based on user's onboarding completion status
            const shouldShowOnboarding = !authState.user.is_onboarded;

            setShowOnboarding(shouldShowOnboarding);
            return;
          }
        } catch (apiError) {
          console.error('API error when fetching user stats:', apiError);
          console.info('User ID being used:', authState.user?.id);
          console.info('Full API error:', apiError);
        }

        // Fallback to mock data with simulation delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        const mockStats = {
          projects: 0, // Mock: user starts with no projects
          activeProjects: 0, // Mock: user starts with no active projects
          analyses: 0, // Mock: user starts with no analyses
          tenderBankItems: 0, // Mock: user starts with no tender bank items
          successRate: 0, // Mock: no success rate initially
          improvementRate: 0, // Mock: no improvement rate initially
          averageResponseScore: 0, // Mock: no average score initially
          timeSavedHours: 0, // Mock: no time saved initially (0 analyses × 13 hours = 0)
          isLoading: false,
        };

        // TODO: For testing, you can temporarily set values to non-zero to test dashboard view:
        // mockStats.projects = 2;
        // mockStats.analyses = 5;
        // mockStats.tenderBankItems = 3;

        setUserStats(mockStats);

        // Show onboarding based on user's onboarding completion status
        const shouldShowOnboarding = !authState.user?.is_onboarded;

        setShowOnboarding(shouldShowOnboarding);
      } catch (error) {
        console.error('Error checking user data:', error);
        // Default to showing dashboard on error
        setShowOnboarding(false);
        setUserStats(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkUserData();
  }, [authState.user?.id, authState.user?.is_onboarded]);

  const handleFormSubmit = async (formData: FormData) => {
    try {
      setLoading(true);
      setError(null);
      setFormData(formData);

      const response = await TenderAnalysisAPI.submitTenderAnalysis(formData);

      if (response.success && response.data) {
        const threadId = response.data.thread_id || response.data.id || 'new-analysis';
        setThreadId(threadId);
        setCurrentStep('analysis');
        navigate(`/tender-response/${threadId}`);
      } else {
        setError(response.error || 'Failed to start analysis');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'new-analysis':
        navigate('/tender-response');
        break;
      case 'upload':
        navigate('/tender-bank');
        break;
      case 'templates':
        // Navigate to templates
        break;
      case 'settings':
        navigate('/settings');
        break;
    }
  };

  const handleStartTour = () => {
    // Start the onboarding tour by navigating to the new analysis form
    console.log('Starting onboarding tour');
    // Focus on the new analysis form to guide user through their first analysis
    const formElement = document.getElementById('new-analysis-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Optionally switch to main dashboard view but keep the form in focus
      setShowOnboarding(false);
    } else {
      // Fallback: just switch to main dashboard
      setShowOnboarding(false);
    }
  };

  // Suppress TS6133 — handleFormSubmit is preserved per the brief because the
  // commented-out InputForm above it may be re-enabled later. Touching this
  // value once keeps the linter quiet without altering behaviour.
  void handleFormSubmit;

  // ---------------------------------------------------------------------------
  // Derived: time-of-day greeting + display name + formatted date stamp
  // ---------------------------------------------------------------------------
  const { greeting, firstName, dateStamp } = useMemo(() => {
    const now = new Date();
    const hour = now.getHours();
    const greetingFor =
      hour < 5
        ? 'Working late'
        : hour < 12
        ? 'Good morning'
        : hour < 17
        ? 'Good afternoon'
        : hour < 22
        ? 'Good evening'
        : 'Working late';
    const first = authState.user?.first_name?.trim() || 'there';

    // TUE · 26 APR 2026 — manual format using Intl primitives.
    const weekday = now.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
    const day = now.getDate();
    const month = now
      .toLocaleDateString('en-GB', { month: 'short' })
      .toUpperCase();
    const year = now.getFullYear();
    const stamp = `${weekday} · ${day} ${month} ${year}`;

    return { greeting: greetingFor, firstName: first, dateStamp: stamp };
  }, [authState.user?.first_name]);

  // Adaptive header status pill — surfaces "where you are" in one phrase.
  const headerStatus = useMemo<{ tone: StatusTone; label: React.ReactNode }>(() => {
    if (userStats.activeProjects > 0) {
      return {
        tone: 'success',
        label: (
          <>
            <strong>{userStats.activeProjects.toLocaleString()}</strong> active{' '}
            {userStats.activeProjects === 1 ? 'project' : 'projects'}
          </>
        ),
      };
    }
    if (userStats.analyses === 0) {
      return { tone: 'primary', label: <>Ready when you are</> };
    }
    return {
      tone: 'primary',
      label: (
        <>
          <strong>{userStats.analyses.toLocaleString()}</strong> analyses ·{' '}
          <strong>{userStats.timeSavedHours.toLocaleString()}</strong>h saved
        </>
      ),
    };
  }, [userStats]);

  // Show loading state while checking user data
  if (showOnboarding === null || userStats.isLoading) {
    return (
      <DashboardContainer>
        <Particles
          particleCount={30}
          particleColor={theme.colors.primary[300]}
          speed={0.3}
          opacity={0.4}
        />
        <ContentWrapper>
          <Container>
            <LoadingShell>
              <LoadingSpinner />
              Loading your dashboard…
            </LoadingShell>
          </Container>
        </ContentWrapper>
      </DashboardContainer>
    );
  }

  // Conditional rendering based on user onboarding state
  if (showOnboarding) {
    return (
      <TourProvider>
        <DashboardContainer>
          <Particles
            particleCount={30}
            particleColor={theme.colors.primary[300]}
            speed={0.3}
            opacity={0.4}
          />
          <ContentWrapper>
            <Container>
              <OnboardingDashboard onStartTour={handleStartTour} />
            </Container>
          </ContentWrapper>
          <TourOverlay />
        </DashboardContainer>
      </TourProvider>
    );
  }

  // Resolve the activity list to render. Real events lead; demo entries pad the
  // panel so a fresh account does not render a near-empty timeline.
  const activities =
    recentActivities.length > 0
      ? padActivities(recentActivities)
      : padActivities(fallbackActivities);

  // The 6-stat array (preserved); first 4 land in the hero grid, last 2
  // become the secondary Performance pill row.
  const stats = getDashboardStats(userStats);
  const heroStatTones: StatTone[] = ['primary', 'success', 'warning', 'accent'];
  const heroStats = stats.slice(0, 4);
  const performanceStats = stats.slice(4); // Improvement Rate + Avg Score

  return (
    <DashboardContainer>
      <Particles
        particleCount={30}
        particleColor={theme.colors.primary[300]}
        speed={0.3}
        opacity={0.4}
      />
      <ContentWrapper>
        <Container>
          {/* ========================= PAGE HEADER ========================= */}
          <PageHeader className="dashboard-header">
            <HeaderLeft>
              <Crumb>Home</Crumb>
              <GreetingTitle>
                {greeting}
                <span className="comma">,</span>
                <span className="name">{firstName}</span>
              </GreetingTitle>
              <Subtitle>
                Your tender command center — track progress, jump back into work, and start a new
                analysis whenever you're ready.
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <StatusPill $tone={headerStatus.tone}>
                <span className="pulse" />
                <span>{headerStatus.label}</span>
              </StatusPill>
              <ActionsRow>
                <PrimaryButton type="button" onClick={() => navigate('/tender-response')}>
                  <Plus size={16} />
                  New project
                </PrimaryButton>
              </ActionsRow>
              <DateStamp>{dateStamp}</DateStamp>
            </HeaderRight>
          </PageHeader>

          {/* ========================= STATS GRID ========================= */}
          {userStats.analyses > 0 && (
            <>
              <StatsGrid className="metrics-grid">
                {heroStats.map((stat, index) => (
                  <StatCard key={stat.label}>
                    <StatIconTile $tone={heroStatTones[index]}>{stat.icon}</StatIconTile>
                    <StatBody>
                      <StatValueWrap>
                        {stat.type === 'percentage' ? (
                          <PercentageCounter value={stat.value} duration={1500} />
                        ) : (
                          <NumberCounter value={stat.value} duration={1500} suffix={stat.suffix} />
                        )}
                      </StatValueWrap>
                      <StatLabel>{stat.label}</StatLabel>
                    </StatBody>
                  </StatCard>
                ))}
              </StatsGrid>

              <PerformanceRow>
                {performanceStats.map(stat => (
                  <PerformancePill key={stat.label}>
                    <span className="icon">{stat.icon}</span>
                    <span className="label">{stat.label}</span>
                    <span className="value">
                      {stat.value.toLocaleString()}
                      {stat.type === 'percentage' ? '%' : stat.suffix || ''}
                    </span>
                  </PerformancePill>
                ))}
              </PerformanceRow>
            </>
          )}

          {/* ========================= TWO-COLUMN BODY ========================= */}
          <BodyGrid>
            {/* Left: Activity timeline */}
            <Panel className="recent-activity-section">
              <Eyebrow>
                <span className="num">01</span>
                <span>·</span>
                <span>Activity</span>
              </Eyebrow>
              <SubHeading>Recent activity</SubHeading>
              <Timeline>
                {activities.map((activity, index) => (
                  <TimelineItem key={activity.id || index}>
                    <TimelineIcon>{getActivityIcon(activity.icon)}</TimelineIcon>
                    <TimelineBody>
                      <TimelineText>{activity.description}</TimelineText>
                      <TimelineTime>{activity.time}</TimelineTime>
                    </TimelineBody>
                  </TimelineItem>
                ))}
              </Timeline>
              <TimelineFooter>
                <TimelineMoreLink
                  type="button"
                  onClick={() => console.log('View all activity — route TBD')}
                  aria-label="View all activity"
                >
                  View all activity
                  <ChevronRight size={14} className="arrow" />
                </TimelineMoreLink>
              </TimelineFooter>
            </Panel>

            {/* Right: Quick actions */}
            <Panel className="quick-actions-panel">
              <Eyebrow>
                <span className="num">02</span>
                <span>·</span>
                <span>Quick actions</span>
              </Eyebrow>
              <SubHeading>Jump back in</SubHeading>
              <QuickActionsList>
                {quickActions.map(action => (
                  <QuickActionCard
                    key={action.action}
                    type="button"
                    onClick={() => handleQuickAction(action.action)}
                  >
                    <QuickActionIcon>{action.icon}</QuickActionIcon>
                    <QuickActionBody>
                      <QuickActionLabel>{action.label}</QuickActionLabel>
                      <QuickActionMeta>{quickActionMetas[action.action] || ''}</QuickActionMeta>
                    </QuickActionBody>
                    <QuickActionArrow className="qa-arrow">
                      <ChevronRight size={16} />
                    </QuickActionArrow>
                  </QuickActionCard>
                ))}
              </QuickActionsList>
            </Panel>
          </BodyGrid>

          {/* ========================= GLOBAL OPPORTUNITIES ========================= */}
          <GlobeHero className="globe-section">
            <GlobeText>
              <GlobeEyebrow>
                <span className="dot" />
                Worldwide
              </GlobeEyebrow>
              <GlobeTitle>Tender activity is global.</GlobeTitle>
              <GlobeSub>
                The AI tracks bidding patterns across regions to refine your responses and surface
                opportunities you'd otherwise miss.
              </GlobeSub>
              <RegionRow>
                <RegionChip>
                  <span className="code">EU</span>
                  <span className="count">1,240 active</span>
                </RegionChip>
                <RegionChip>
                  <span className="code">UK</span>
                  <span className="count">480 active</span>
                </RegionChip>
                <RegionChip>
                  <span className="code">US</span>
                  <span className="count">2,110 active</span>
                </RegionChip>
                <RegionChip>
                  <span className="code">APAC</span>
                  <span className="count">670 active</span>
                </RegionChip>
              </RegionRow>
            </GlobeText>
            <GlobeWrap>
              <Globe size={180} rotationSpeed={0.005} />
            </GlobeWrap>
          </GlobeHero>

          {/* ========================= ELSEWHERE QUICK LINKS ========================= */}
          <ElsewhereSection>
            <ElsewhereHead>
              <Eyebrow>
                <span className="num">03</span>
                <span>·</span>
                <span>Elsewhere</span>
              </Eyebrow>
              <SubHeading>Other parts of the app</SubHeading>
            </ElsewhereHead>
            <ElsewhereGrid>
              <ElsewhereCard
                type="button"
                onClick={() => navigate('/knowledge-base')}
                aria-label="Open Knowledge Base"
              >
                <ElsewhereIcon>
                  <Database size={20} />
                </ElsewhereIcon>
                <ElsewhereBody>
                  <ElsewhereTitle>Knowledge base</ElsewhereTitle>
                  <ElsewhereMeta>
                    Documents your AI uses to draft responses — overviews, CVs, case studies.
                  </ElsewhereMeta>
                </ElsewhereBody>
                <ElsewhereArrow className="ew-arrow">
                  <ChevronRight size={18} />
                </ElsewhereArrow>
              </ElsewhereCard>
              <ElsewhereCard
                type="button"
                onClick={() => navigate('/tender-bank')}
                aria-label="Open Tender Bank"
              >
                <ElsewhereIcon>
                  <Folder size={20} />
                </ElsewhereIcon>
                <ElsewhereBody>
                  <ElsewhereTitle>Tender bank</ElsewhereTitle>
                  <ElsewhereMeta>
                    Past tenders the AI mines for patterns, voice, and evidence on every draft.
                  </ElsewhereMeta>
                </ElsewhereBody>
                <ElsewhereArrow className="ew-arrow">
                  <ChevronRight size={18} />
                </ElsewhereArrow>
              </ElsewhereCard>
            </ElsewhereGrid>
          </ElsewhereSection>

          {/* The InputForm flow remains commented out in the JSX — preserved for
              future re-enablement. The handler + import + AppContext destructure
              are kept intact above. */}
          {/*
          <DashboardGrid>
            <BentoGridItem
              title="New Analysis"
              description="Upload tender documents to start AI-powered analysis and response generation."
              icon={<Plus size={24} />}
            >
              <div id="new-analysis-form">
                <InputForm onSubmit={handleFormSubmit} />
              </div>
            </BentoGridItem>
          </DashboardGrid>
          */}
        </Container>
      </ContentWrapper>
    </DashboardContainer>
  );
};

export default HomePage;
